import React, { useState, useEffect, useMemo } from 'react';
import { FireIcon, MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { GasPod } from '../types/gas';
import { gasPodService } from '../services/gasService';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LoadingScreen from '../components/common/LoadingScreen';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';

const GasPods: React.FC = () => {
  const { t } = useLanguage();
  const [pods, setPods] = useState<GasPod[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const loadPods = async () => {
    setLoading(true);
    try {
      const data = await gasPodService.getAllGasPods();
      setPods(data);
    } catch (error) {
      console.error('Error loading gas pods:', error);
      toast.error(t('gas.messages.loadError', { name: t('gas.sections.pods') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPods();
  }, []);

  const filteredPods = useMemo(() => {
    if (!searchTerm.trim()) return pods;
    const term = searchTerm.toLowerCase();
    return pods.filter(pod =>
      pod.podId.toLowerCase().includes(term) ||
      pod.name?.toLowerCase().includes(term) ||
      pod.address?.city?.toLowerCase().includes(term) ||
      pod.address?.street?.toLowerCase().includes(term) ||
      pod.meterSerialNumber?.toLowerCase().includes(term)
    );
  }, [pods, searchTerm]);

  const totalPages = Math.ceil(filteredPods.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPods = filteredPods.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'active': return 'Aktív';
      case 'inactive': return 'Inaktív';
      case 'terminated': return 'Megszüntetett';
      default: return status || 'Ismeretlen';
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="space-y-4">
      <CommonPageHeader
        title={t('gas.sections.pods')}
        subtitle={t('gas.list.subtitle', { count: pods.length })}
        icon={FireIcon}
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('gas.list.search.pods')}
          />
        </div>
        <button
          onClick={loadPods}
          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4 mr-2" />
          {t('gas.list.refresh')}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.pod.id')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.pod.name')} / {t('gas.pod.address')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.pod.meterSerialNumber')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.pod.capacityKw')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.pod.status')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentPods.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                    {t('gas.list.noResults')}
                  </td>
                </tr>
              ) : (
                currentPods.map(pod => (
                  <tr key={pod.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium text-gray-900">{pod.podId}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{pod.name || '—'}</p>
                        <p className="text-xs text-gray-500">{pod.address?.city}, {pod.address?.street}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700">{pod.meterSerialNumber || '—'}</p>
                      <p className="text-xs text-gray-500">{pod.meterType || ''}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700">
                        {pod.capacityKw ? `${pod.capacityKw} kW` : '—'}
                        {pod.maxHourlyCapacityM3 ? ` / ${pod.maxHourlyCapacityM3} m³/h` : ''}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        pod.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : pod.status === 'inactive'
                            ? 'bg-gray-100 text-gray-700'
                            : 'bg-red-100 text-red-700'
                      }`}>
                        {getStatusLabel(pod.status)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={filteredPods.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        indexOfFirstItem={indexOfFirstItem}
        indexOfLastItem={indexOfLastItem}
      />
    </div>
  );
};

export default GasPods;
